import { logger, LoggerLevel } from "../utils/logger";
import type { LoadRemoteModuleOptions, RemoteComponentType } from "./types";
import { ensureWebpackShareScope } from "./webpackShareScope";
import {
  getRemoteDefinition,
  type RemoteId
} from "./remoteRegistry";

export class RemoteLoadError extends Error {
  readonly remoteEntryUrl: string;

  readonly cause?: unknown;

  constructor(message: string, remoteEntryUrl: string, cause?: unknown) {
    super(message);
    this.name = "RemoteLoadError";
    this.remoteEntryUrl = remoteEntryUrl;
    this.cause = cause;
  }
}

const scriptPromises = new Map<string, Promise<void>>();
const modulePromises = new Map<string, Promise<unknown>>();

const DEFAULT_TIMEOUT_MS = 30_000;
const DEFAULT_RETRIES = 2;

function loadRemoteEntryScript(
  remoteEntryUrl: string,
  timeoutMs: number
): Promise<void> {
  const existing = scriptPromises.get(remoteEntryUrl);
  if (existing !== undefined) {
    return existing;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = remoteEntryUrl;
    script.type = "text/javascript";
    script.async = true;

    const timer = window.setTimeout(() => {
      script.remove();
      scriptPromises.delete(remoteEntryUrl);
      reject(
        new RemoteLoadError(
          `Timed out loading remote entry after ${timeoutMs}ms`,
          remoteEntryUrl
        )
      );
    }, timeoutMs);

    script.onload = () => {
      window.clearTimeout(timer);
      resolve();
    };

    script.onerror = () => {
      window.clearTimeout(timer);
      script.remove();
      scriptPromises.delete(remoteEntryUrl);
      reject(
        new RemoteLoadError("Failed to load remote entry script", remoteEntryUrl)
      );
    };

    document.head.appendChild(script);
  });

  scriptPromises.set(remoteEntryUrl, promise);
  return promise;
}

async function loadWithRetry<T>(
  operation: () => Promise<T>,
  retries: number
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (attempt === retries) {
        break;
      }
      logger.log(
        `Remote load attempt ${attempt + 1} failed; retrying`,
        LoggerLevel.WARN,
        { error },
        "microfrontends"
      );
    }
  }

  throw lastError;
}

function resolveRemoteExport<T>(moduleExport: unknown): T {
  if (
    moduleExport &&
    typeof moduleExport === "object" &&
    "default" in moduleExport
  ) {
    return (moduleExport as { default: T }).default;
  }

  return moduleExport as T;
}

export async function loadRemoteModule<T = unknown>(
  remoteEntryUrl: string,
  federationName: string,
  modulePath: string,
  options: LoadRemoteModuleOptions = {}
): Promise<T> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const retries = options.retries ?? DEFAULT_RETRIES;
  const cacheKey = `${remoteEntryUrl}::${federationName}::${modulePath}`;

  const cached = modulePromises.get(cacheKey);
  if (cached !== undefined) {
    return cached as Promise<T>;
  }

  const loadPromise = loadWithRetry(async () => {
    await loadRemoteEntryScript(remoteEntryUrl, timeoutMs);

    const container = (window as unknown as Record<string, unknown>)[
      federationName
    ] as
      | {
          init?: (shareScope: Record<string, unknown>) => Promise<void>;
          get?: (path: string) => Promise<() => unknown>;
        }
      | undefined;

    if (!container?.get || !container.init) {
      scriptPromises.delete(remoteEntryUrl);
      throw new RemoteLoadError(
        `Remote container "${federationName}" not found on window`,
        remoteEntryUrl
      );
    }

    const shareScope = await ensureWebpackShareScope();
    await container.init(shareScope);
    const factory = await container.get(modulePath);
    const moduleExport = factory();

    return resolveRemoteExport<T>(moduleExport);
  }, retries);

  modulePromises.set(cacheKey, loadPromise);

  try {
    return await loadPromise;
  } catch (error) {
    modulePromises.delete(cacheKey);
    logger.log(
      "Failed to load remote module",
      LoggerLevel.ERROR,
      { remoteEntryUrl, federationName, modulePath, error },
      "microfrontends"
    );
    throw error;
  }
}

export async function loadRemoteComponent<P = Record<string, unknown>>(
  remoteId: RemoteId,
  options?: LoadRemoteModuleOptions
): Promise<RemoteComponentType<P>> {
  const definition = getRemoteDefinition(remoteId);

  return loadRemoteModule<RemoteComponentType<P>>(
    definition.getRemoteEntryUrl(),
    definition.federationName,
    definition.module,
    options
  );
}

/** Test-only reset for module/script caches. */
export function resetRemoteModuleCacheForTests(): void {
  scriptPromises.clear();
  modulePromises.clear();
}
